import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { attachmentsApi, type Submittal, type SubmittalRound } from '@/lib/apiClient';
import { StateView, Badge, FileAttachments } from '@/components';
import type { BadgeTone, StateViewQuery } from '@/components';
import { fmtDate, fmtDateTime, fmtInstantDate } from '@/lib/dates';
import { fmtSentAt } from './lib/sentAt';
import { describeRecipients } from './contacts/RecipientsField';
import { ATTACHMENT_NAME_HINT } from '@/lib/graphUpload';
import {
  attachmentDocNumber, attachmentStatus, filesByRound, latestRound,
  revisionLabel, roundStage, roundState,
} from './submittalRounds';

/**
 * The submittal's review rounds: Rev 0 is the package as it came from the
 * subcontractor, and each Revise & Resubmit or Rejected that comes back leads to the
 * next. For the round you pick it shows the three dates (received from the sub, sent
 * for review, returned), the outcome, and the two sets of files: the package that went
 * out, and the reviewer's marked-up copy.
 *
 * Replaces the single Job Folder card this screen used to have. Uploads still go to the
 * project's Job Folder through <FileAttachments>; what is new is that each one is
 * recorded against a round and a side of it.
 *
 * Reviewers cannot upload here or into the Job Folder (links to it do not open outside
 * the company), so the returned side holds what E&W staff attach when the markup comes
 * back by email.
 *
 * Uses the `sub-section*` / `sub-help` classes SubmittalsTab declares; this only ever
 * renders from that tab's detail view.
 */

const css = `
  .sub-rounds-tabs { display: flex; gap: var(--ew-s-2); flex-wrap: wrap; }
  .sub-rounds-tab {
    display: flex; flex-direction: column; align-items: flex-start; gap: 3px;
    background: #fff; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md);
    padding: 7px 12px; cursor: pointer; color: var(--ew-n-600); text-align: left;
    font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1 var(--ew-font);
    transition: all var(--ew-dur-fast) var(--ew-ease);
  }
  .sub-rounds-tab:hover { border-color: var(--ew-n-300); color: var(--ew-dark); }
  .sub-rounds-tab[aria-selected="true"] { background: var(--ew-dark); border-color: var(--ew-dark); color: #fff; }
  .sub-rounds-tab-state { font: var(--ew-fw-regular) 11px/1 var(--ew-font); opacity: 0.75; }
  .sub-round-row {
    display: flex; justify-content: space-between; gap: var(--ew-s-4); padding: 7px 0;
    border-bottom: 1px solid var(--ew-n-100);
    font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font);
  }
  .sub-round-row:last-child { border-bottom: 0; }
  .sub-round-row span:first-child { color: var(--ew-n-500); }
  .sub-round-row span:last-child { color: var(--ew-dark); font-weight: var(--ew-fw-medium); text-align: right; }
  .sub-round-files { display: grid; gap: var(--ew-s-2); margin: 0; padding: 0; list-style: none; }
  .sub-round-link {
    font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1.3 var(--ew-font);
    color: var(--ew-quality); text-decoration: none; word-break: break-all;
  }
  .sub-round-link:hover { text-decoration: underline; }
  .sub-round-mono {
    font-family: var(--ew-font-mono, ui-monospace, monospace);
    background: var(--ew-n-100); border-radius: var(--ew-r-sm);
    padding: 2px 6px; color: var(--ew-n-700); word-break: break-all;
  }
`;

const OUTCOME_TONE: Record<NonNullable<SubmittalRound['outcome']>, BadgeTone> = {
  approved: 'success',
  'approved-as-noted': 'success',
  'revise-resubmit': 'warning',
  rejected: 'warning',
};

function stateTone(round: SubmittalRound): BadgeTone {
  const stage = roundStage(round);
  if (stage === 'returned') return round.outcome ? OUTCOME_TONE[round.outcome] : 'neutral';
  return stage === 'in-review' ? 'info' : 'neutral';
}

export default function SubmittalRoundsCard({
  sub,
  project,
  canEdit,
  query,
}: {
  sub: Submittal;
  project: { jobNumber: string; jobFolderUrl: string | null };
  /** Whether this user may attach or remove files (projects.edit, and the job not locked). */
  canEdit: boolean;
  query: StateViewQuery<SubmittalRound[]>;
}) {
  return (
    <div className="pw-card">
      <style>{css}</style>
      <h2 className="pw-card-hd">Review rounds</h2>
      {/* isEmpty is never true: an empty list is a state of its own (see Rounds), not a
          blank card, because the submittal's files still have to be shown. */}
      <StateView query={query} isEmpty={() => false}>
        {(rounds) => <Rounds sub={sub} project={project} canEdit={canEdit} rounds={rounds} />}
      </StateView>
    </div>
  );
}

function Rounds({
  sub, project, canEdit, rounds,
}: {
  sub: Submittal;
  project: { jobNumber: string; jobFolderUrl: string | null };
  canEdit: boolean;
  rounds: SubmittalRound[];
}) {
  const [picked, setPicked] = useState<number | null>(null);
  // Undefined only when no round is recorded at all (see the empty case below).
  const selected = rounds.find((r) => r.id === picked) ?? latestRound(rounds);

  // A round that has just been opened is the one to look at, even if an older one was
  // being read when Send Revision was clicked.
  useEffect(() => { setPicked(null); }, [rounds.length]);

  // The same list FileAttachments reads (one request, one cache entry), here only to
  // find files that are on no round: the previous release uploaded them without one.
  const files = useQuery({
    queryKey: ['attachments', 'submittal', sub.id],
    queryFn: () => attachmentsApi.list('submittal', sub.id),
    enabled: !!project.jobFolderUrl,
  });
  const { unassigned } = filesByRound(files.data ?? [], rounds);

  const unassignedSection = unassigned.length > 0 && (
    <div className="sub-section">
      <p className="sub-section-label">Files not on a round</p>
      <ul className="sub-round-files">
        {unassigned.map((f) => (
          <li key={f.id}>
            <a className="sub-round-link" href={f.webUrl} target="_blank" rel="noreferrer">{f.fileName} ↗</a>
          </li>
        ))}
      </ul>
    </div>
  );

  // A submittal that existed before the rounds did, and that nothing has written a
  // round for yet: the next send, decision or revision does, and so does the migration's
  // backfill, which is run after the deploy. There is nowhere to attach a NEW file until
  // then, but the files it already has are listed, so nothing the old Job Folder card
  // showed goes missing.
  if (!selected) {
    return (
      <>
        <div className="sub-section">
          <p className="sub-help">
            No review round is recorded for this submittal yet. The first is created the next
            time it is sent to the reviewer, decided or sent for revision.
          </p>
        </div>
        {unassignedSection}
      </>
    );
  }

  // The ARIA tab pattern: ← → move between the tabs (wrapping), Home and End go to the
  // ends, and only the selected tab is a Tab stop. Selecting follows focus.
  const onTabKeys = (e: React.KeyboardEvent) => {
    // A modified key is somebody else's shortcut (Alt+← is the browser's Back), not ours.
    if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!step && e.key !== 'Home' && e.key !== 'End') return;
    e.preventDefault();
    const at = rounds.findIndex((r) => r.id === selected.id);
    const to = e.key === 'Home' ? 0
      : e.key === 'End' ? rounds.length - 1
      : (at + step + rounds.length) % rounds.length;
    const next = rounds[to];
    setPicked(next.id);
    document.getElementById(`sub-round-tab-${next.id}`)?.focus();
  };

  const original = selected.revision === 0;
  const docNumber = attachmentDocNumber(sub.submittalNumber, selected.revision);
  const filesFor = (kind: 'submitted' | 'returned') => (
    <FileAttachments
      bare
      canEdit={canEdit}
      entityType="submittal"
      entityId={sub.id}
      jobFolderUrl={project.jobFolderUrl}
      subfolder="Submittals"
      jobNumber={project.jobNumber}
      docNumber={docNumber}
      shortDescSource={sub.title}
      status={attachmentStatus(kind, sub.status)}
      round={{ id: selected.id, kind }}
      uploadLabel={kind === 'returned' ? '＋ Upload returned markup' : original ? '＋ Upload original package' : '＋ Upload resubmitted package'}
    />
  );

  return (
    <>
      <div className="sub-section">
        <div className="sub-rounds-tabs" role="tablist" aria-label="Review rounds" onKeyDown={onTabKeys}>
          {rounds.map((r) => (
            <button
              key={r.id}
              type="button"
              role="tab"
              id={`sub-round-tab-${r.id}`}
              aria-selected={r.id === selected.id}
              tabIndex={r.id === selected.id ? 0 : -1}
              aria-controls="sub-round-panel"
              className="sub-rounds-tab"
              onClick={() => setPicked(r.id)}
            >
              {revisionLabel(r.revision)}
              <span className="sub-rounds-tab-state">{roundState(r)}</span>
            </button>
          ))}
        </div>
      </div>

      <div id="sub-round-panel" role="tabpanel" aria-labelledby={`sub-round-tab-${selected.id}`}>
        <div className="sub-section">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ew-s-2)', marginBottom: 'var(--ew-s-2)' }}>
            <span style={{ font: 'var(--ew-fw-semibold) 15px/1 var(--ew-font)' }}>{revisionLabel(selected.revision)}</span>
            <Badge label={roundState(selected)} tone={stateTone(selected)} />
          </div>
          <div className="sub-round-row"><span>Received from sub</span><span>{fmtDate(selected.receivedAt)}</span></div>
          {/* Eastern with the zone named, as the Reviewer notification card shows the same
              moment (#626): a viewer's own zone would read one send as two different times. */}
          <div className="sub-round-row">
            <span>Sent for review</span><span>{fmtSentAt(selected.sentAt)}</span>
          </div>
          {/* Who this round went to, as confirmed (#598 step 2). */}
          {(selected.sentTo?.length ?? 0) > 0 && (
            <div className="sub-round-row">
              <span>Sent to</span><span>{describeRecipients(selected.sentTo!)}</span>
            </div>
          )}
          <div className="sub-round-row" title={selected.returnedAt ? fmtDateTime(selected.returnedAt) : undefined}>
            <span>Returned</span><span>{fmtInstantDate(selected.returnedAt)}</span>
          </div>
          {/* The outcome is the badge above, so it is not repeated as a row. */}
          {selected.note && (
            <div className="sub-round-row"><span>Reviewer comments</span><span>{selected.note}</span></div>
          )}
        </div>

        {project.jobFolderUrl ? (
          <>
            <div className="sub-section">
              <p className="sub-section-label">{original ? 'Original package' : 'Resubmitted package'}</p>
              {filesFor('submitted')}
            </div>
            <div className="sub-section">
              <p className="sub-section-label">Returned with comments</p>
              {filesFor('returned')}
            </div>
          </>
        ) : (
          <div className="sub-section">
            <p className="sub-help">
              This project has no Job Folder linked, so files can't be kept on the submittal record
              yet. Link one under Projects → edit.
            </p>
          </div>
        )}
      </div>

      {unassignedSection}

      {project.jobFolderUrl && (
        <div className="sub-section" style={{ borderBottom: 0 }}>
          {canEdit && (
            <p className="sub-help">
              Files upload to the project's Job Folder (▸ Submittals) and stay linked here as the
              record of each round. A returned markup goes under the round it answers: it arrives by
              email, since reviewers can't open the Job Folder. Emails to outside reviewers still
              need the files attached in Outlook — Job Folder links won't open for anyone outside the
              company.
            </p>
          )}
          <p className="sub-help" style={{ marginTop: 'var(--ew-s-2)' }}>
            Removing a file only removes the link here; it stays in SharePoint. Names follow{' '}
            <span className="sub-round-mono">{ATTACHMENT_NAME_HINT}</span>, and a later round adds its
            revision to the number (SUB-002-R1).
          </p>
        </div>
      )}
    </>
  );
}
